import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, List, ListItem, ListItemText, ListItemButton, Paper, Chip, IconButton, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Helmet } from '../lib/HelmetShim';
import { client } from '../api/Api';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadMessages();
    loadUnreadCount();
  }, []);

  const loadMessages = async (pageNum = 0) => {
    try {
      if (pageNum === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const res = await client.get('/messages', { params: { page: pageNum, size: 20 } });
      if (pageNum === 0) {
        setMessages(res.data.messages || []);
      } else {
        setMessages(prev => [...prev, ...(res.data.messages || [])]);
      }
      setHasMore(res.data.hasNext || false);
      setPage(pageNum);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida užkraunant žinutes.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await client.get('/messages/unread-count');
      setUnreadCount(res.data.unreadCount || 0);
    } catch (e) {
      // Ignore errors for unread count
    }
  };

  const handleMessageClick = async (messageId) => {
    try {
      const res = await client.get(`/messages/${messageId}`);
      setSelectedMessage(res.data);
      // Reload messages to update read status
      loadMessages(page);
      loadUnreadCount();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida užkraunant žinutę.');
      }
    }
  };

  const handleReprocessReceipt = async (messageId, e) => {
    e.stopPropagation();
    if (!window.confirm('Ar tikrai norite per\'procesinti šį kvitą? Visi produktai bus ištraukti iš naujo.')) {
      return;
    }
    setReprocessing(true);
    try {
      await client.post(`/messages/${messageId}/reprocess-receipt`);
      // Reload message
      const res = await client.get(`/messages/${messageId}`);
      setSelectedMessage(res.data);
      // Reload messages list
      await loadMessages(page);
      alert('Kvitas sėkmingai per\'procesintas!');
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        alert('Klaida per\'procesinant kvitą: ' + (e.response?.data?.error || e.message));
      }
    } finally {
      setReprocessing(false);
    }
  };

  const handleDelete = async (messageId, e) => {
    e.stopPropagation();
    if (!window.confirm('Ar tikrai norite ištrinti šią žinutę?')) {
      return;
    }
    try {
      await client.delete(`/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      loadUnreadCount();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida trinant žinutę.');
      }
    }
  };

  const handleMarkAsRead = async (messageId, e) => {
    e.stopPropagation();
    try {
      await client.post(`/messages/${messageId}/mark-read`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, isRead: true } : m));
      loadUnreadCount();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida pažymint žinutę kaip perskaitytą.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle ISO format with or without timezone
      let date;
      if (typeof dateString === 'string') {
        // If it's already in ISO format, use it directly
        if (dateString.includes('T')) {
          date = new Date(dateString);
        } else {
          // If it's just a date string, try to parse it
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Dabar';
      if (diffMins < 60) return `Prieš ${diffMins} min.`;
      if (diffHours < 24) return `Prieš ${diffHours} val.`;
      if (diffDays < 7) return `Prieš ${diffDays} d.`;
      return date.toLocaleDateString('lt-LT', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  const getPreview = (textContent, htmlContent) => {
    if (htmlContent) {
      const div = document.createElement('div');
      div.innerHTML = htmlContent;
      const text = div.textContent || div.innerText || '';
      return text.substring(0, 100) + (text.length > 100 ? '...' : '');
    }
    if (textContent) {
      return textContent.substring(0, 100) + (textContent.length > 100 ? '...' : '');
    }
    return 'Nėra turinio';
  };

  if (error) {
    return <div style={{ maxWidth: 800, margin: '20px auto' }}><Alert severity="error">{error}</Alert></div>;
  }

  if (loading) {
    return (
      <Box sx={{ maxWidth: 800, margin: '20px auto', textAlign: 'center', padding: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Žinutės – Tikrai</title>
        <meta name="description" content="Gautos žinutės" />
      </Helmet>
      <Container maxWidth="lg" className="container" sx={{ px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Žinutės
          </Typography>
          {unreadCount > 0 && (
            <Chip label={unreadCount} color="error" size="small" />
          )}
        </Box>

        {messages.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Nėra gautų žinučių
            </Typography>
          </Paper>
        ) : (
          <>
            <Paper sx={{ mb: 2 }}>
              <List sx={{ p: 0 }}>
                {messages.map((message) => (
                  <ListItem
                    key={message.id}
                    disablePadding
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: message.isRead ? 'transparent' : 'action.hover',
                      '&:hover': { backgroundColor: 'action.selected' }
                    }}
                  >
                    <ListItemButton onClick={() => handleMessageClick(message.id)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: message.isRead ? 400 : 700,
                                flex: 1
                              }}
                            >
                              {message.subject || '(Be temos)'}
                            </Typography>
                            {!message.isRead && (
                              <Chip label="Nauja" color="primary" size="small" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Nuo: {message.fromEmail}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getPreview(message.textContent, message.htmlContent)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {formatDate(message.receivedAt)}
                            </Typography>
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {!message.isRead && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMarkAsRead(message.id, e)}
                            title="Pažymėti kaip perskaitytą"
                          >
                            <MarkEmailReadIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(message.id, e)}
                          color="error"
                          title="Ištrinti"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>

            {hasMore && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => loadMessages(page + 1)}
                  disabled={loadingMore}
                >
                  {loadingMore ? <CircularProgress size={20} /> : 'Įkelti daugiau'}
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Message detail dialog */}
        <Dialog
          open={!!selectedMessage}
          onClose={() => setSelectedMessage(null)}
          maxWidth="md"
          fullWidth
        >
          {selectedMessage && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">{selectedMessage.subject || '(Be temos)'}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedMessage(null)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Data:</strong> {selectedMessage.receivedAt ? (() => {
                      try {
                        const date = new Date(selectedMessage.receivedAt);
                        if (isNaN(date.getTime())) return selectedMessage.receivedAt;
                        return date.toLocaleString('lt-LT', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                      } catch (e) {
                        return selectedMessage.receivedAt;
                      }
                    })() : ''}
                  </Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
                  {selectedMessage.htmlContent ? (
                    <Box
                      sx={{
                        '& img': { maxWidth: '100%', height: 'auto' },
                        '& table': { width: '100%', borderCollapse: 'collapse', margin: '16px 0' },
                        '& table td, & table th': { padding: '8px', border: '1px solid #ddd' },
                        '& pre': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                        '& *': { maxWidth: '100%' },
                      }}
                      dangerouslySetInnerHTML={{ __html: selectedMessage.htmlContent }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {selectedMessage.textContent || 'Nėra turinio'}
                    </Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedMessage(null)}>Uždaryti</Button>
                {!selectedMessage.isRead && (
                  <Button
                    onClick={() => handleMarkAsRead(selectedMessage.id, { stopPropagation: () => {} })}
                    startIcon={<MarkEmailReadIcon />}
                  >
                    Pažymėti kaip perskaitytą
                  </Button>
                )}
                <Button
                  onClick={() => handleDelete(selectedMessage.id, { stopPropagation: () => {} })}
                  color="error"
                  startIcon={<DeleteIcon />}
                >
                  Ištrinti
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </>
  );
}

